/**
 * Batches are the canonical inventory model. Every quantity change should
 * flow through here: addBatch on increment, setItemTotalQuantity on adjust.
 *
 * pantry_items.current_quantity is a CACHED SUM — never write to it directly.
 * A trigger (refresh_pantry_item_quantity) maintains it from active batches.
 *
 * pantry_items.current_expiration_date is the EARLIEST active batch expiry,
 * also maintained by the trigger.
 *
 * Status conventions (matches the rest of the codebase):
 *   - 'active'  → contributes to current_quantity sum
 *   - 'used'    → consumed, does not count
 * Recipes.tsx, HomeRecipeTab.tsx, and ItemDetailSheet all flip 'active' → 'used'
 * on consume; we do the same here for consistency.
 */

import { supabase } from '../lib/supabase'

export type BatchRow = {
  id:           string
  item_id:      string
  household_id: string
  quantity:     number
  expiry_date:  string | null
  status:       'active' | 'used' | string
  created_at:   string
  purchased_at: string | null
  store_name:   string | null
  source:       string | null
}

// Canonical source tags. The Phase 5 ItemDetailSheet UI labels each batch
// based on this column, so use a known value rather than free-form text.
export type BatchSource =
  | 'unpack'           // came in via finish-unpack (on-list confirm or impulse)
  | 'manual_adjust'    // qty-pill plus, or detail-sheet quantity stepper
  | 'manual_add'       // detail-sheet "add batch" button
  | 'barcode_scan'     // bulk barcode scanner
  | 'recipe_override'  // user toggled a recipe ingredient to "I have this"
  | 'legacy'           // pre-Phase-0, source backfilled to this

/**
 * All active batches for an item, FIFO-ordered: earliest expiry first,
 * then oldest created_at as a tiebreaker. Batches with null expiry sort
 * to the end so they get consumed last (no rush, they don't go bad).
 */
export async function getActiveBatches(itemId: string): Promise<BatchRow[]> {
  const { data, error } = await supabase
    .from('item_batches')
    .select('*')
    .eq('item_id', itemId)
    .eq('status',  'active')
    .order('expiry_date', { ascending: true,  nullsFirst: false })
    .order('created_at',  { ascending: true })
  if (error) throw error
  return (data ?? []) as BatchRow[]
}

/**
 * Append a new batch. Used by receipt scan, manual restock, AddItemModal.
 * The trigger updates pantry_items.current_quantity automatically after.
 *
 * householdId is REQUIRED — the column is NOT NULL on item_batches and
 * inserts without it fail.
 */
export async function addBatch(input: {
  itemId:      string
  householdId: string
  quantity:    number
  expiryDate:  string | null
  purchasedAt?: string | null
  source?:     BatchSource
  storeName?:  string | null
}): Promise<BatchRow> {
  // quantity column is INTEGER per the schema. Round at the boundary —
  // callers passing fractional quantities lose precision rather than crash.
  const safeQty = Math.max(0, Math.round(input.quantity))
  const { data, error } = await supabase
    .from('item_batches')
    .insert({
      item_id:      input.itemId,
      household_id: input.householdId,
      quantity:     safeQty,
      expiry_date:  input.expiryDate,
      status:       'active',
      purchased_at: input.purchasedAt ?? new Date().toISOString(),
      source:       input.source ?? null,
      store_name:   input.storeName ?? null,
    })
    .select('*')
    .single()
  if (error || !data) throw error ?? new Error('Failed to add batch')
  return data as BatchRow
}

/**
 * Adjust an item's total quantity by writing through batches.
 *
 *  - Increment (newTotal > current): append a new batch for the difference.
 *  - Decrement (newTotal < current): consume FIFO from oldest expiry first.
 *    Each fully consumed batch is marked status='used' (quantity 0); a
 *    partially consumed batch has its quantity reduced in place.
 *  - No change: no-op.
 *
 * Returns the new effective total. The trigger updates pantry_items
 * shortly after each batch mutation.
 */
export async function setItemTotalQuantity(input: {
  itemId:                string
  householdId:           string
  newTotal:              number
  expiryDateForNewBatch?: string | null
  source?:               BatchSource    // override for the synthetic batch on increment
}): Promise<number> {
  const safeTotal = Math.max(0, Math.round(input.newTotal))
  const batches   = await getActiveBatches(input.itemId)
  const current   = batches.reduce((s, b) => s + Number(b.quantity), 0)
  const delta     = safeTotal - current

  if (delta === 0) return current

  if (delta > 0) {
    // Increment via synthetic batch. Defaults to 'manual_adjust' since the
    // qty pill / quantity stepper is the dominant caller; receipt/import
    // paths should call addBatch directly with their own source tag.
    await addBatch({
      itemId:      input.itemId,
      householdId: input.householdId,
      quantity:    delta,
      expiryDate:  input.expiryDateForNewBatch ?? null,
      source:      input.source ?? 'manual_adjust',
    })
    return safeTotal
  }

  // Decrement — FIFO consume from oldest expiry batch
  let remaining = -delta
  for (const batch of batches) {
    if (remaining <= 0) break
    const batchQty = Number(batch.quantity)
    if (batchQty <= remaining) {
      const { error } = await supabase
        .from('item_batches')
        .update({ status: 'used', quantity: 0 })
        .eq('id', batch.id)
      if (error) throw error
      remaining -= batchQty
    } else {
      const { error } = await supabase
        .from('item_batches')
        .update({ quantity: batchQty - remaining })
        .eq('id', batch.id)
      if (error) throw error
      remaining = 0
    }
  }
  return safeTotal
}
